import classNames from 'classnames'
import {
  type KeyboardEventHandler,
  type KeyboardEvent,
  type MouseEventHandler,
  type MouseEvent,
  StrictMode,
  useCallback,
  forwardRef,
} from 'react'
import { useTheme } from '@opallabs/hooks'
import * as styles from './styles'
import { type SwitchProps } from './types'

export { type SwitchProps }
export { selectors as switchSelectors } from './styles'

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked = false,
      checkedLabel = 'on',
      className,
      id,
      onToggle,
      readonly = false,
      uncheckedLabel = 'off',
      ...otherProps
    },
    ref
  ) => {
    const theme = useTheme()

    const handleEvent = useCallback(
      (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()

        onToggle?.(!checked)
      },
      [checked, onToggle]
    )

    const onClick: MouseEventHandler<HTMLButtonElement> = handleEvent

    const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
      if (![' ', 'Enter'].includes(e.key)) return

      handleEvent(e)
    }

    return (
      <StrictMode>
        <button
          className={classNames(
            'lapidary-toggle',
            className,
            styles.selectors.container
          )}
          css={[styles.container, styles.containerState(theme)]}
          aria-checked={checked}
          disabled={readonly}
          id={id}
          onClick={onClick}
          onKeyDown={onKeyDown}
          ref={ref}
          role="switch"
          tabIndex={0}
          // @note `<button>`s default to `type=submit`. Without setting `type=button`, if a `<Switch>` has the first
          // `<button>` in a form hitting Enter will toggle the switch instead of submitting the form.
          type="button"
          {...otherProps}
        >
          <span className={styles.selectors.tray} />
          <span className={styles.selectors.handle} />
          <span className={styles.selectors.label}>
            {checked ? checkedLabel : uncheckedLabel}
          </span>
        </button>
      </StrictMode>
    )
  }
)
Switch.displayName = 'Lapidary: Switch'
